/**
 * Frido Selector Pills runtime.
 *
 * One placement renders ONE block of text pills, decided entirely by the
 * placement's config (no detection): the option axis the liquid matched from
 * the `axis` prop — picking a pill selects a variant on this product.
 * Product-group navigate pills are fpgfrd01, a separate component.
 *
 * Richer layouts live in sibling snippets (ftyfrd02 image cards, fcbfrd02
 * priced rows). This component is pills-only by contract.
 */
;(() => {
  if (window.__sai_ftyfrd01_initialized__) return
  window.__sai_ftyfrd01_initialized__ = true

  const SNIPPET_ID = 'ftyfrd01'
  const TAG = 'sai-ftyfrd01'
  const FEATURE_SLUG = 'variant_picker'

  function esc(s) {
    return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }
  function readPool(node) {
    for (let i = 0; i < node.children.length; i++) {
      const c = node.children[i]
      if (c.tagName === 'SCRIPT' && c.hasAttribute('data-spectrum-snippet-pool')) {
        try { return JSON.parse(c.textContent) } catch (e) { return {} }
      }
    }
    return {}
  }

  // per-variant badge construct (value_display badges: { label, bg, fg, icon }). Icon
  // is a URL (<img>) or a short glyph. Colours are authored data written into
  // inline style, esc()-guarded against attribute breakout. Missing label -> skip.
  function badgePill(b) {
    if (!b || !b.label) return ''
    const bg = b.bg || '#000000'
    const fg = b.fg || '#ffffff'
    const icon = b.icon
      ? (/^(https?:)?\/\//.test(b.icon)
          ? `<img src="${esc(b.icon)}" alt="" loading="lazy" decoding="async">`
          : `<span aria-hidden="true">${esc(b.icon)}</span>`)
      : ''
    return `<span class="sai-ftyfrd01__badge" style="background:${esc(bg)};color:${esc(fg)}">${icon}${esc(b.label)}</span>`
  }
  function badgesHtml(list) {
    return Array.isArray(list) && list.length
      ? `<span class="sai-ftyfrd01__badges">${list.map(badgePill).join('')}</span>`
      : ''
  }

  function optKey(axis) { return 'option' + axis.position }
  function variantsFor(pool, sel) {
    return (pool.product.variants || []).filter((v) =>
      Object.entries(sel).every(([k, val]) => !val || v[k] === val))
  }

  const El = class extends HTMLElement {
    connectedCallback() {
      // A sibling axis snippet (Size / Colour / etc.) selecting a variant must
      // re-point our selection: adopt the new variant's value for each axis we
      // render and re-render. Our own picks set _curVariantId first (see _pick)
      // so an event we fired is a no-op here. Bound outside the _init guard so a
      // re-mounted element (DOM move / SDK re-mount) re-subscribes.
      if (!this._onVariantChange) {
        this._onVariantChange = (e) => {
          const id = e && e.detail && e.detail.variantId
          if (id == null || !this._pool || !this._pool.product) return
          const axes = this._pool.product.axes || []
          if (!axes.length) return // group-mode instances don't track a variant
          if (String(id) === String(this._curVariantId)) return
          this._curVariantId = id
          const nv = (this._pool.product.variants || []).find((v) => String(v.id) === String(id))
          if (nv) for (const axis of axes) { const k = optKey(axis); if (nv[k] != null) axis.selected = nv[k] }
          this._render()
        }
      }
      window.addEventListener('spectrum:variant-change', this._onVariantChange)
      if (this._init) return
      this._init = true
      this._track = () => {}
    }
    disconnectedCallback() {
      if (this._onVariantChange) window.removeEventListener('spectrum:variant-change', this._onVariantChange)
    }
    setAnalytics(t) { this._track = typeof t === 'function' ? t : () => {} }

    applyVariant(_content, pool) {
      this._pool = pool && typeof pool === 'object' ? pool : {}
      const p = this._pool.product
      if (!p) return
      if ((!p.axes || !p.axes.length)) return
      if (this._curVariantId == null) this._curVariantId = p.current_variant_id
      // A re-read of the frozen SSR pool (SDK re-bind) resets axis.selected to the
      // server default; re-point each axis to the live current variant so a prior
      // pick isn't silently reverted.
      const cur = this._currentVariant()
      if (cur) for (const axis of p.axes || []) { const k = optKey(axis); if (cur[k] != null) axis.selected = cur[k] }
      this._render()
      this._bind()
    }

    _props() { return (this._pool && this._pool.props) || {} }

    _currentVariant() {
      const prod = this._pool && this._pool.product
      if (!prod) return null
      const id = this._curVariantId != null ? this._curVariantId : prod.current_variant_id
      return (prod.variants || []).find((v) => String(v.id) === String(id)) || null
    }

    // Pin EVERY axis to the shopper's current full-variant selection, then override
    // only the axis being changed. Preserves sibling pickers' axes (Size / Colour)
    // and any other axis this instance renders — the cross-axis correctness the
    // size/colour pickers already use. Without this, only this axis is pinned and
    // the others resolve to first-available, silently clobbering the shopper's pick.
    _selections(axis, value) {
      const sel = {}
      const cur = this._currentVariant()
      if (cur) for (const k of ['option1', 'option2', 'option3']) if (cur[k] != null) sel[k] = cur[k]
      sel[optKey(axis)] = value
      return sel
    }

    _render() {
      const p = this._pool.product
      const props = this._props()
      let html = ''

      for (const axis of p.axes || []) {
        const label = `<div class="sai-ftyfrd01__label"><span class="sai-ftyfrd01__label-key">${esc(props.label_prefix || axis.name + (props.label_separator || ' :'))}</span><span class="sai-ftyfrd01__label-val">${esc(axis.selected || '')}</span></div>`
        const fit = axis.values.length > 2 ? 'content' : 'grid'
        let body = `<div class="sai-ftyfrd01__cards" data-fit="${fit}">`
        for (const item of axis.values) {
          // Own-axis availability: strike a value only if it has NO available variant AT ALL.
          // A cross-axis pin here is WRONG for a scoping axis (e.g. mattress Category, whose
          // values carry DISJOINT size sets) — pinning the current size would falsely strike
          // every other category that lacks it, making available categories look unbuyable.
          // The greedy cross-axis resolution that preserves the shopper's other picks lives in
          // _pick, not the strike; tapping a genuinely-OOS value still surfaces OOS on the ATC.
          const avail = variantsFor(this._pool, { [optKey(axis)]: item.v }).some((v) => v.available)
          // oos_display prop (Studio): strike (default) | hide | disable — the liquid SSR mirrors this.
          const oosMode = props.oos_display || 'strike'
          if (!avail && oosMode === 'hide') continue
          const disabled = !avail && oosMode === 'disable'
          const cls = ['sai-ftyfrd01__card', item.v === axis.selected ? 'is-sel' : '', avail ? '' : 'is-oos', disabled ? 'is-disabled' : ''].filter(Boolean).join(' ')
          const badgeHtml = props.show_badges !== false ? badgesHtml(item.badges) : ''
          body += `<span class="sai-ftyfrd01__cell">${badgeHtml}<button type="button" class="${cls}"${disabled ? ' aria-disabled="true"' : ' data-action="pick"'} data-value="${esc(item.v)}" data-axis="${esc(axis.name)}">${esc(item.v)}</button></span>`
        }
        body += '</div>'
        html += `<div class="sai-ftyfrd01__axis" data-axis="${esc(axis.name)}">${label}${body}</div>`
      }


      this.innerHTML = `<div class="sai-ftyfrd01__inner">${html}</div>`
    }

    _bind() {
      if (this._clickBound) return
      this._clickBound = true
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-action]')
        if (!t) return
        if (t.dataset.action === 'pick') this._pick(t.dataset.axis, t.dataset.value)
      })
    }

    _pick(axisName, value) {
      const p = this._pool.product
      const axis = (p.axes || []).find((a) => a.name === axisName)
      if (!axis || axis.selected === value) return
      axis.selected = value
      // Resolve preserving as many of the shopper's other-axis picks (Size / Colour)
      // as possible. Start from the fully-pinned selection; if that combination
      // doesn't exist, keep the tapped axis and greedily re-add each sibling that
      // still leaves a match — so switching an axis whose new value lacks only ONE
      // of the current siblings (e.g. a mattress Category with different sizes but
      // the same colours) keeps the colour and drops only the impossible size,
      // rather than resetting every other axis to first-available.
      let sel = this._selections(axis, value)
      let vs = variantsFor(this._pool, sel)
      if (!vs.length) {
        sel = { [optKey(axis)]: value }
        const cur = this._currentVariant()
        if (cur) for (const k of ['option1', 'option2', 'option3']) {
          if (k === optKey(axis) || cur[k] == null) continue
          const trial = { ...sel, [k]: cur[k] }
          if (variantsFor(this._pool, trial).length) sel = trial
        }
        vs = variantsFor(this._pool, sel)
      }
      const variant = vs.find((v) => v.available) || vs[0] || null
      if (variant) {
        // mark first so our own variant-change listener treats this as a no-op
        this._curVariantId = variant.id
        try {
          const url = new URL(window.location.href)
          url.searchParams.set('variant', String(variant.id))
          window.history.replaceState({}, '', url.toString())
        } catch (e) {}
        window.dispatchEvent(new CustomEvent('spectrum:variant-change', { detail: { variantId: variant.id } }))
        // Sync the theme product form's hidden variant input. This snippet
        // replaces a native option picker, and product-form.js submits whatever
        // is in `[name="id"]` — without this, add-to-cart uses the default
        // variant regardless of the picked type. Scope to forms whose current
        // value is one of THIS product's variants so upsell/other-product forms
        // are never touched.
        try {
          const _vids = {}
          ;(this._pool.product.variants || []).forEach((vv) => { _vids[String(vv.id)] = 1 })
          document.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
            if (_vids[String(input.value)]) {
              input.value = String(variant.id)
              input.dispatchEvent(new Event('change', { bubbles: true }))
            }
          })
        } catch (e) {}
      }
      this._render()
      this._track(`${FEATURE_SLUG}:select_type`, { axis: axisName, value })
    }
  }

  if (!customElements.get(TAG)) customElements.define(TAG, El)

  function bindAll() {
    const api = window.__spectrumAi && window.__spectrumAi.snippet
    for (const node of document.querySelectorAll(`[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`)) {
      const el = node.querySelector(TAG)
      if (!el) continue
      // First paint straight from the server-rendered pool — the pool's props
      // are the instance's default config, so rendering never waits on the
      // SDK (which may load after DOMContentLoaded or resolve asynchronously).
      if (!el._painted) {
        const initPool = readPool(node)
        if (initPool && (initPool.product || initPool.bundle || initPool.axis || initPool.buying_options)) { el._painted = true; el.applyVariant({}, initPool) }
      }
      if (api && typeof api.bind === 'function' && !el._wrapBound) {
        el._wrapBound = true
        const handles = api.bind(node, (ctx) => {
          const e = node.querySelector(TAG)
          if (e) e.applyVariant(ctx.variant ? ctx.variant.content : {}, readPool(node))
        })
        if (handles) el.setAnalytics(handles.track)
      }
    }
    for (const el of document.querySelectorAll(TAG)) {
      if (el._applied || el._wrapBound || el.closest('[data-spectrum-instance-id]')) continue
      const parent = el.closest('[data-spectrum-lq-snippet]') || el.parentElement
      if (!parent) continue
      const pool = readPool(parent)
      if (pool && pool.product) { el._applied = true; el.applyVariant({}, pool) }
    }
  }

  function schedule() {
    bindAll()
    // the SDK can load after us — retry until it binds (or give up quietly)
    let tries = 0
    const t = setInterval(() => {
      bindAll()
      const api = window.__spectrumAi && window.__spectrumAi.snippet
      if ((api && typeof api.bind === 'function') || ++tries > 20) clearInterval(t)
    }, 500)
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true })
  } else {
    schedule()
  }

  // The storefront SDK can re-mount slot wrappers after load (conflict
  // resolution / vis flips) — a replaced wrapper holds a fresh unpainted
  // element, so rebind on DOM changes, debounced.
  const __saiRelevant = (records) => {
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n.nodeType !== 1) continue
        if ((n.matches && n.matches(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"], [data-spectrum-lq-snippet]`)) ||
            (n.querySelector && n.querySelector(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"]`))) return true
      }
    }
    return false
  }
  const __saiMo = new MutationObserver((records) => {
    if (__saiMo._t || !__saiRelevant(records)) return
    __saiMo._t = setTimeout(() => { __saiMo._t = null; bindAll() }, 150)
  })
  __saiMo.observe(document.documentElement, { childList: true, subtree: true })
  // Injected-content rebinding survives the observer's time window (quick-add drawer).
  window.__saiRebind = window.__saiRebind || {}
  window.__saiRebind['ftyfrd01'] = bindAll
  setTimeout(function () { try { __saiMo.disconnect() } catch (e) {} }, 30000)
})()
